// ไฟล์: frontend/src/pages/admin/AdminGradeLevelsPage.js
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import "./AdminStyles.css";
import AdminSidebar from "../../components/layout/AdminSidebar";

const AdminGradeLevelsPage = ({ user }) => {
  const [gradeLevels, setGradeLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    description: "",
  });
  const [isEditing, setIsEditing] = useState(false);

  const tableRef = useRef(null);
  const dataTableRef = useRef(null);

  // ดึงข้อมูลระดับชั้นทั้งหมด
  useEffect(() => {
    const fetchGradeLevels = async () => {
      try {
        const response = await axios.get("/grade-levels");
        setGradeLevels(response.data.gradeLevels);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching grade levels:", error);
        setLoading(false);
      }
    };

    fetchGradeLevels();
  }, []);

  // เริ่มต้น DataTable หลังจากข้อมูลโหลดเสร็จ
  useEffect(() => {
    if (!loading && tableRef.current) {
      // ถ้ามี DataTable อยู่แล้ว ให้ทำลายก่อน
      if (dataTableRef.current) {
        dataTableRef.current.destroy();
        dataTableRef.current = null;
      }

      // รอให้ DOM อัปเดตก่อนสร้าง DataTable
      setTimeout(() => {
        if (window.$ && window.$.fn.DataTable && tableRef.current) {
          dataTableRef.current = window.$(tableRef.current).DataTable({
            language: {
              processing: "กำลังประมวลผล...",
              search: "ค้นหา:",
              lengthMenu: "แสดง _MENU_ รายการ",
              info: "แสดง _START_ ถึง _END_ จาก _TOTAL_ รายการ",
              infoEmpty: "แสดง 0 ถึง 0 จาก 0 รายการ",
              infoFiltered: "(กรองจากทั้งหมด _MAX_ รายการ)",
              loadingRecords: "กำลังโหลดข้อมูล...",
              zeroRecords: "ไม่พบข้อมูล",
              emptyTable: "ไม่มีข้อมูลในตาราง",
              paginate: {
                first: "หน้าแรก",
                previous: "ก่อนหน้า",
                next: "ถัดไป",
                last: "หน้าสุดท้าย",
              },
              aria: {
                sortAscending: ": เรียงจากน้อยไปมาก",
                sortDescending: ": เรียงจากมากไปน้อย",
              },
            },
            responsive: true,
          });
        }
      }, 100);
    }

    // Cleanup function
    return () => {
      if (dataTableRef.current) {
        dataTableRef.current.destroy();
        dataTableRef.current = null;
      }
    };
  }, [loading, gradeLevels]);

  // จัดการการเปลี่ยนแปลงในฟอร์ม
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ส่งข้อมูลฟอร์ม
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (isEditing) {
        // อัพเดทระดับชั้น
        await axios.put("/grade-levels", formData);
        Swal.fire({
          icon: "success",
          title: "อัพเดทสำเร็จ",
          text: "อัพเดทระดับชั้นเรียบร้อยแล้ว",
          confirmButtonText: "ตกลง",
        });
      } else {
        // สร้างระดับชั้นใหม่
        await axios.post("/grade-levels", {
          name: formData.name,
          description: formData.description,
        });
        Swal.fire({
          icon: "success",
          title: "เพิ่มสำเร็จ",
          text: "เพิ่มระดับชั้นเรียบร้อยแล้ว",
          confirmButtonText: "ตกลง",
        });
      }

      // รีเซ็ตฟอร์มและดึงข้อมูลใหม่
      setFormData({ id: "", name: "", description: "" });
      setIsEditing(false);

      // ดึงข้อมูลระดับชั้นใหม่
      const response = await axios.get("/grade-levels");
      setGradeLevels(response.data.gradeLevels);
    } catch (error) {
      console.error("Error submitting form:", error);
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: "ไม่สามารถบันทึกข้อมูลได้",
        confirmButtonText: "ตกลง",
      });
    }
  };

  // แก้ไขระดับชั้น
  const handleEdit = (gradeLevel) => {
    setFormData({
      id: gradeLevel.id,
      name: gradeLevel.name,
      description: gradeLevel.description || "",
    });
    setIsEditing(true);

    // เลื่อนไปที่ฟอร์ม
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // ลบระดับชั้น
  const handleDelete = async (id) => {
    try {
      const result = await Swal.fire({
        icon: "warning",
        title: "ยืนยันการลบ",
        text: "คุณต้องการลบระดับชั้นนี้ใช่หรือไม่?",
        showCancelButton: true,
        confirmButtonText: "ลบ",
        cancelButtonText: "ยกเลิก",
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
      });

      if (result.isConfirmed) {
        await axios.delete(`/grade-levels/${id}`);

        Swal.fire({
          icon: "success",
          title: "ลบสำเร็จ",
          text: "ลบระดับชั้นเรียบร้อยแล้ว",
          confirmButtonText: "ตกลง",
        });

        // อัพเดทรายการระดับชั้น
        const updatedGradeLevels = gradeLevels.filter(
          (level) => level.id !== id
        );
        setGradeLevels(updatedGradeLevels);
      }
    } catch (error) {
      console.error("Error deleting grade level:", error);
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: "ไม่สามารถลบระดับชั้นได้",
        confirmButtonText: "ตกลง",
      });
    }
  };

  // ยกเลิกการแก้ไข
  const handleCancel = () => {
    setFormData({ id: "", name: "", description: "" });
    setIsEditing(false);
  };

  // แสดงสถานะการโหลด
  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">กำลังโหลด...</span>
        </div>
        {/* <p>กำลังโหลดข้อมูล...</p> */}
      </div>
    );
  }

  return (
    <div className="admin-page">
      <AdminSidebar user={user} />
      <div className="admin-header">
        <h1 className="admin-title">
          <i className="fas fa-graduation-cap"></i> จัดการระดับชั้น
        </h1>
        <div className="admin-breadcrumb">
          <Link to="/admin/dashboard">แดชบอร์ด</Link> /
          <span>จัดการระดับชั้น</span>
        </div>
      </div>

      <div className="admin-card">
        <div className="admin-card-header">
          <h2>{isEditing ? "แก้ไขระดับชั้น" : "เพิ่มระดับชั้นใหม่"}</h2>
        </div>
        <div className="admin-card-body">
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label htmlFor="name" className="form-label">
                  ชื่อระดับชั้น
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="col-md-6 mb-3">
                <label htmlFor="description" className="form-label">
                  คำอธิบาย
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="d-flex gap-2">
              <button type="submit" className="btn btn-primary">
                <i className={`fas ${isEditing ? "fa-save" : "fa-plus"}`}></i>{" "}
                {isEditing ? "บันทึก" : "เพิ่ม"}
              </button>
              {isEditing && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCancel}
                >
                  <i className="fas fa-times"></i> ยกเลิก
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      <div className="admin-card mt-4">
        <div className="admin-card-header">
          <h2>รายการระดับชั้น</h2>
        </div>
        <div className="admin-card-body">
          <div className="table-responsive">
            <table className="table table-striped" ref={tableRef}>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>ชื่อระดับชั้น</th>
                  <th>คำอธิบาย</th>
                  <th>วันที่สร้าง</th>
                  <th>จัดการ</th>
                </tr>
              </thead>
              <tbody>
                {gradeLevels.map((gradeLevel) => (
                  <tr key={gradeLevel.id}>
                    <td>{gradeLevel.id}</td>
                    <td>{gradeLevel.name}</td>
                    <td>{gradeLevel.description}</td>
                    <td>
                      {new Date(gradeLevel.created_at).toLocaleDateString(
                        "th-TH"
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-sm btn-warning"
                          onClick={() => handleEdit(gradeLevel)}
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(gradeLevel.id)}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminGradeLevelsPage;
